import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { useState } from "react";
import { CREATE_ALTERNATE_ID_INFO } from "./api";
import { CnForm, CnFormData } from "./CnForm";
import { isDuplicateCnError, isValidCn } from "./utils";

interface AddCnDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AddCnDialog({ onClose, onSuccess }: AddCnDialogProps): JSX.Element {
  const alerts = useAlerts();
  const [formData, setFormData] = useState<CnFormData>({
    cnValue: "",
    tenantId: null,
    description: "",
  });

  const [createAlternateIdInfo, { loading }] = useMutation(
    CREATE_ALTERNATE_ID_INFO,
    {
      onCompleted: () => {
        alerts.success("CN added successfully");
        onSuccess();
      },
      onError: (error) => {
        if (isDuplicateCnError(error)) {
          alerts.error(`CN ${formData.cnValue} already exists`);
        } else {
          alerts.error("Failed to add CN", error);
        }
      },
    }
  );

  function handleSubmit(): void {
    if (!formData.tenantId) {
      return;
    }
    createAlternateIdInfo({
      variables: {
        input: {
          alternateIdValue: formData.cnValue,
          tenantId: formData.tenantId,
          description: formData.description || undefined,
        },
      },
    });
  }

  const isValid = isValidCn(formData.cnValue) && formData.tenantId;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add New CN"
      maxWidth="md"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        disabled: !isValid,
        saveText: "Add CN",
      }}
    >
      <CnForm data={formData} onChange={setFormData} />
    </Dialog>
  );
}
